/**
 * Legal-form auto-evolution tick — pure helper extracted from
 * coreSlice.nextDay (02/06/2026 night refactor).
 *
 * Watches the rolling 12-month revenue against the legal form thresholds.
 * If exceeded → schedules a January 1st automatic change (`updatedCompany`)
 * and optionally fires a pedagogical quiz on the first detection.
 * Otherwise → returns an "approach" descriptor when the player is between
 * 80–100 % of a threshold so the caller can fire a milestone-gated
 * pre-warning email.
 *
 * Pure : no side effects, no `set()`. The caller (nextDay) decides how to
 * apply the resulting descriptors.
 */

import { LEGAL_FORMS, checkLegalFormEvolution, checkThresholdApproach } from '../../data/legalForms';
import { pickFiscalQuiz } from '../../data/fiscalQuizzes';

/**
 * @param {Object} state - current game state (read-only)
 * @param {Object} ctx
 * @param {Array}  ctx.updatedMonthlyHistory - the freshly-closed monthly history
 * @param {number} ctx.newYear - the next-tick year
 * @returns {{
 *   updatedCompany: object|null,        // new company patch (only set when mustChange)
 *   pendingFiscalQuiz: object|null,     // quiz to fire (only on first mustChange)
 *   adminMessage: object|null,          // fallback admin warning when no quiz fires
 *   approach: object|null,              // 80%-of-threshold descriptor for milestone trigger
 *   approachMessage: object|null,       // message to push if milestone fires
 * }}
 */
export function runLegalFormEvolutionTick(state, ctx) {
  const empty = {
    updatedCompany: null,
    pendingFiscalQuiz: null,
    adminMessage: null,
    approach: null,
    approachMessage: null,
  };
  try {
    const updatedMonthlyHistory = ctx.updatedMonthlyHistory || [];
    const newYear = ctx.newYear;
    const last12Months = updatedMonthlyHistory.slice(-12);
    const ca12mGlissant = last12Months.reduce((s, m) => s + (m.totalRevenue || 0), 0);
    const currentForm = state.company?.legalForm || 'ei';
    const currentRegime = state.company?.fiscalRegime || 'simplifie';
    const evolution = checkLegalFormEvolution(currentForm, currentRegime, ca12mGlissant);
    const hasPending = !!state.company?.pendingLegalFormChange;

    if (evolution.mustChange && !hasPending) {
      // Plan the change for January 1st of next year.
      const plannedYear = newYear + 1;
      const updatedCompany = {
        ...state.company,
        pendingLegalFormChange: {
          targetForm: evolution.targetForm,
          targetRegime: evolution.targetRegime,
          plannedYear,
          detectedAtDay: (state.totalDays || 0) + 1,
        },
      };

      // Pedagogical quiz: only on the FIRST time this threshold is crossed.
      const quizId = pickFiscalQuiz(currentForm, currentRegime);
      const alreadyPassed = (state.company?.fiscalQuizPassed || []).includes(quizId);
      if (quizId && !alreadyPassed) {
        return {
          ...empty,
          updatedCompany,
          pendingFiscalQuiz: {
            quizId,
            plannedYear,
            detectedAtDay: (state.totalDays || 0) + 1,
          },
        };
      }
      // Fallback admin notification.
      return {
        ...empty,
        updatedCompany,
        adminMessage: {
          senderType: 'urssaf',
          senderName: 'Centre des Impôts',
          tone: 'urgent',
          kind: 'warning',
          subject: 'Évolution de statut juridique obligatoire',
          body: `${evolution.reason}\n\nVous pouvez anticiper ce changement dès maintenant depuis la page Fiscalité → Statut Juridique. Sinon, la bascule sera effectuée automatiquement au 1er janvier ${plannedYear}.`,
          ttlDays: 60,
        },
      };
    }

    if (!evolution.mustChange && !hasPending) {
      // Threshold approach (≥ 80 % but not yet over).
      const approach = checkThresholdApproach(currentForm, currentRegime, ca12mGlissant);
      if (approach) {
        return {
          ...empty,
          approach,
          approachMessage: {
            senderType: 'urssaf',
            senderName: 'Administration fiscale — Pôle conformité',
            tone: 'formal',
            kind: 'warning',
            subject: `Anticipation : approche du seuil ${approach.label}`,
            body:
              `Bonjour,\n\n${approach.reason}\n\n` +
              `Cet email est un signal d'anticipation : aucun changement n'est encore obligatoire à ce stade. ` +
              `Vous disposez encore d'une marge pour vous préparer.\n\n` +
              `Rendez-vous sur la page Fiscalité → Statut Juridique pour consulter le détail et amorcer la transition de manière volontaire si vous le souhaitez.\n\n` +
              `Cordialement,\nL'Administration fiscale`,
            ttlDays: 45,
          },
        };
      }
    }
    return empty;
  } catch (e) {
    // The monthly close orchestrator MUST never crash on this auxiliary
    // check — losing 1 month of fiscal-evolution detection is acceptable,
    // losing a save isn't.
    // eslint-disable-next-line no-console
    console.warn('[legalFormEvolutionTick] failed:', e);
    return empty;
  }
}

// Re-export so callers don't need to import LEGAL_FORMS separately when
// they only consume this tick result.
export { LEGAL_FORMS };
